/**
 * DynamicForm Component
 *
 * A higher-level form component that renders multiple DynamicFormField components
 * with support for:
 * - Conditional visibility/requirement
 * - Form state management
 * - Field ordering
 * - Validation
 */

import { Button } from '@sunrise-ui/primitives';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { StyleSheet, View } from 'react-native';

import { DynamicFormField } from './DynamicFormField';
import {
  useConditionalFields,
  type FieldWithConditions,
} from './hooks/useConditionalFields';
import type { FieldValue, FormErrors, FormValues } from './types';

type Props = {
  /** Array of field configurations */
  fields: FieldWithConditions[];
  /** Initial form values */
  initialValues?: FormValues;
  /** Callback when form is submitted */
  onSubmit?: (values: FormValues) => void | Promise<void>;
  /** Callback when form values change */
  onChange?: (values: FormValues) => void;
  /** Custom submit button text */
  submitButtonText?: string;
  /** Whether to show the submit button */
  showSubmitButton?: boolean;
  /** Whether the form is currently submitting */
  isSubmitting?: boolean;
  /** Whether the form is disabled */
  disabled?: boolean;
  /** Custom validation function */
  validate?: (values: FormValues) => FormErrors;
  /** Style for the form container */
  style?: object;
};

export type DynamicFormHandle = {
  /** Validate the form and show errors. Returns true if valid. */
  validate: () => boolean;
  /** Get current form values */
  getValues: () => FormValues;
};

/**
 * Validates required fields and returns error messages
 */
function validateRequiredFields(
  fields: FieldWithConditions[],
  values: FormValues,
  isFieldRequired: (fieldId: string) => boolean,
  isFieldVisible: (fieldId: string) => boolean,
): FormErrors {
  const errors: FormErrors = {};

  for (const field of fields) {
    const fieldId = field.id;

    // Skip validation for hidden fields
    if (!isFieldVisible(fieldId)) {
      continue;
    }

    // Check required fields
    if (isFieldRequired(fieldId)) {
      const value = values[fieldId];
      const isEmpty =
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0);

      if (isEmpty) {
        errors[fieldId] = `${field.label} is required`;
      }
    }
  }

  return errors;
}

export const DynamicForm = forwardRef<DynamicFormHandle, Props>(
  (
    {
      fields,
      initialValues = {},
      onSubmit,
      onChange,
      submitButtonText = 'Submit',
      showSubmitButton = true,
      isSubmitting = false,
      disabled = false,
      validate,
      style,
    },
    ref,
  ) => {
    const [formValues, setFormValues] = useState<FormValues>(initialValues);
    const [errors, setErrors] = useState<FormErrors>({});
    const [touched, setTouched] = useState<Record<string, boolean>>({});

    // Get conditional field states
    const {
      visibleFields,
      isFieldRequired,
      isFieldDisabled,
      isFieldVisible,
      fieldStates,
    } = useConditionalFields(fields, formValues);

    // Sort fields by orderIndex
    const sortedFields = useMemo(() => {
      return [...visibleFields].sort((a, b) => {
        const orderA = a.orderIndex ?? 0;
        const orderB = b.orderIndex ?? 0;
        return orderA - orderB;
      });
    }, [visibleFields]);

    // Handle field value change
    const handleFieldChange = useCallback(
      (fieldId: string, value: FieldValue) => {
        const newValues = { ...formValues, [fieldId]: value };
        setFormValues(newValues);

        // Mark field as touched
        if (!touched[fieldId]) {
          setTouched((prev) => ({ ...prev, [fieldId]: true }));
        }

        // Clear error for this field when value changes
        if (errors[fieldId]) {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[fieldId];
            return newErrors;
          });
        }

        // Notify parent of changes
        onChange?.(newValues);
      },
      [formValues, touched, errors, onChange],
    );

    // Validate form
    const validateForm = useCallback((): boolean => {
      // Built-in required validation
      const requiredErrors = validateRequiredFields(
        fields,
        formValues,
        isFieldRequired,
        isFieldVisible,
      );

      // Custom validation
      const customErrors = validate?.(formValues) ?? {};

      const allErrors = { ...requiredErrors, ...customErrors };
      setErrors(allErrors);

      // Mark all fields as touched on validation attempt
      const allTouched: Record<string, boolean> = {};
      for (const field of fields) {
        allTouched[field.id] = true;
      }
      setTouched(allTouched);

      return Object.keys(allErrors).length === 0;
    }, [fields, formValues, isFieldRequired, isFieldVisible, validate]);

    // Expose methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        validate: validateForm,
        getValues: () => formValues,
      }),
      [validateForm, formValues],
    );

    // Handle form submission
    const handleSubmit = useCallback(async () => {
      if (disabled || isSubmitting) {
        return;
      }

      const isValid = validateForm();
      if (!isValid) {
        return;
      }

      await onSubmit?.(formValues);
    }, [disabled, isSubmitting, validateForm, onSubmit, formValues]);

    return (
      <View style={[styles.container, style]}>
        <View style={styles.fieldsContainer}>
          {sortedFields.map((field) => {
            const fieldState = fieldStates[field.id];
            const fieldError = touched[field.id] ? errors[field.id] : undefined;

            return (
              <DynamicFormField
                key={field.id}
                field={{
                  ...field,
                  required: fieldState?.required ?? field.required,
                  disabled:
                    disabled ||
                    fieldState?.disabled ||
                    isFieldDisabled(field.id),
                }}
                value={formValues[field.id] as FieldValue}
                onChange={(value) => handleFieldChange(field.id, value)}
                error={fieldError}
              />
            );
          })}
        </View>

        {showSubmitButton && (
          <View style={styles.submitContainer}>
            <Button
              onPress={handleSubmit}
              disabled={disabled || isSubmitting}
              loading={isSubmitting}
            >
              {submitButtonText}
            </Button>
          </View>
        )}
      </View>
    );
  },
);

DynamicForm.displayName = 'DynamicForm';

const styles = StyleSheet.create({
  container: {
    gap: 16,
  },
  fieldsContainer: {
    gap: 20,
  },
  submitContainer: {
    marginTop: 8,
  },
});
